import { isValidElement } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";
import { H3 } from "../../components/Heading";
import { Paragraph } from "../../components/Paragraph";
import { Box } from "../../components/Box";

/** The API returns "-" as a placeholder for fields the individual didn't provide. */
export const isProvided = (value?: string | null): boolean =>
  Boolean(value) && value !== "-";

/** Formats an address date to the project's dd/MM/yyyy convention. */
export const formatAddressDate = (value?: string | null): string => {
  if (!isProvided(value)) return "";
  const parsed = DateTime.fromISO(value as string);
  return parsed.isValid ? parsed.toFormat("dd/MM/yyyy") : (value as string);
};

/** Joins provided address parts with a space; "Not provided" when none are set. */
export const joinAddressParts = (parts: Array<string | null | undefined>): string => {
  const provided = parts.filter(isProvided);
  return provided.length ? provided.join(" ") : "Not provided";
};

export const Heading = styled(H3)`
  ${({ theme: { fontSizes } }) => `
    font-size: ${fontSizes[2]};
  `}
`;

export const TitleHeading = styled(H3)`
  ${({ theme: { fontSizes } }) => `
    font-size: ${fontSizes[3]};
    word-break: break-all;
  `}
`;

export const Container = styled.div`
  ${({ theme: { space } }) => `
    padding: ${space[4]};
    height: calc(100vh - 215px);
    overflow-y: auto;
    padding-bottom: ${space[5]}
  `}
`;

export const ChecksBar = styled.div<{
  variant: "green" | "orange" | "gray";
}>`
  ${({ theme: { colors, space }, variant }) => `
    height: 5px;
    width: 100%;
    border-radius: 12px;
    margin-top: ${space[2]};
    margin-bottom: ${space[2]};
    background-color: ${
      variant === "green"
        ? colors.green.primary
        : variant === "orange"
        ? colors.orange[50]
        : colors.gray[40]
    };
  `}
`;

export const ViewLink = styled(Box)`
  ${({ theme: { colors, fontFamily, fontSizes } }) => `
  font-family: ${fontFamily};
  font-size: ${fontSizes[2]};
  color: ${colors.black};
  text-decoration: none;
  cursor: pointer;
  &:hover {
      color: ${colors.gray[70]};
    }
  `}
`;

export const CountBox = styled(Box)`
  ${({ theme: { colors, fontSizes, fontFamily } }) => `
    background-color: ${colors.red.primary};
    color: ${colors.white};
    font-size: ${fontSizes[0]};
    font-family: ${fontFamily};
    padding: 1px 6px;
    border-radius: 6px;
  `}
`;

const renderCardLineChild = (child: any): any => {
  if (child === null || child === undefined || child === "") return "-";
  if (isValidElement(child)) return child;
  if (Array.isArray(child)) return child.map(renderCardLineChild);
  if (
    typeof child === "string" ||
    typeof child === "number" ||
    typeof child === "boolean"
  ) {
    return child;
  }
  if (typeof child === "object") {
    return (
      child.status ||
      child.name ||
      child.label ||
      child.title ||
      child.description ||
      child.id ||
      "-"
    );
  }

  return String(child);
};

export const CardLine = ({ children, ...styleProps }: any) => (
  <Paragraph fontSize={1} color="gray.60" my={0} {...styleProps}>
    {renderCardLineChild(children)}
  </Paragraph>
);
